import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../lib/api';
import { Bell, Save, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const AdminAlertBannerPage = () => {
  const [alert, setAlert] = useState({ enabled: false, message: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await adminAPI.getSettings();
        const site = res.data.site_settings || {};
        setAlert({ enabled: site.alert_enabled || false, message: site.alert_message || '' });
      } catch { toast.error('Failed to load'); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminAPI.updateSiteSettings({ alert_enabled: alert.enabled, alert_message: alert.message });
      toast.success('Alert settings saved!');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-2 border-[#1A73E8] border-t-transparent rounded-full" /></div>;

  return (
    <div className="space-y-6 animate-fade-in-up max-w-3xl">
      <div>
        <h1 className="text-2xl font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>Alert Banner</h1>
        <p className="text-[#5F6368]">Display a scrolling alert message across the site header</p>
      </div>

      <div className="dashboard-card">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-[#FCE8E6] rounded-xl flex items-center justify-center">
              <Bell className="w-6 h-6 text-[#D93025]" />
            </div>
            <div>
              <h3 className="font-semibold text-[#202124]">Alert Banner</h3>
              <p className="text-sm text-[#5F6368]">{alert.enabled ? 'Banner is visible to users' : 'Banner is hidden'}</p>
            </div>
          </div>
          <button onClick={() => setAlert({ ...alert, enabled: !alert.enabled })}
            className={`w-14 h-7 rounded-full transition-colors ${alert.enabled ? 'bg-[#D93025]' : 'bg-[#DADCE0]'}`}
            data-testid="alert-toggle">
            <div className={`w-6 h-6 rounded-full bg-white shadow transition-transform ${alert.enabled ? 'translate-x-7' : 'translate-x-0.5'}`} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#5F6368] mb-1">Alert Message</label>
            <textarea value={alert.message} onChange={(e) => setAlert({ ...alert, message: e.target.value })}
              className="input-google min-h-[120px]" placeholder="Important announcement for all users..."
              data-testid="alert-message" />
          </div>

          {alert.enabled && alert.message && (
            <div className="p-4 bg-[#FEF7E0] border border-[#F9AB00] rounded-lg overflow-hidden">
              <p className="text-xs font-medium text-[#5F6368] mb-2">Live Preview:</p>
              <div className="overflow-hidden bg-[#FEF7E0] rounded py-1">
                <p className="text-sm text-[#202124] whitespace-nowrap animate-marquee font-medium">{alert.message}</p>
              </div>
            </div>
          )}

          <button onClick={handleSave} disabled={saving} className="btn-google flex items-center gap-2" data-testid="save-alert-btn">
            {saving ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminAlertBannerPage;
